import { useState, useCallback, useEffect } from 'react';
import { VobAuthCode, VobAuthCodesParams } from './types';
import { getVobAuthCodes } from './vobAuthCodesService';

interface UseVobAuthCodesReturn {
  authCodes: VobAuthCode[];
  loading: boolean;
  error: Error | null;
  totalCount: number;
  totalPages: number;
  refetch: () => void;
}

export const useVobAuthCodes = ({
  page,
  pageSize,
  sortBy,
  sortOrder,
  authCode,
  policyNumber,
}: VobAuthCodesParams): UseVobAuthCodesReturn => {
  const [authCodes, setAuthCodes] = useState<VobAuthCode[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);
  const [totalCount, setTotalCount] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(1);

  const fetchAuthCodes = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await getVobAuthCodes({
        page,
        pageSize,
        sortBy,
        sortOrder,
        authCode,
        policyNumber,
      });

      if (response.success) {
        setAuthCodes(response.data);
        setTotalCount(response.pagination.total);
        setTotalPages(response.pagination.totalPages);
      } else {
        throw new Error('Failed to fetch authorization codes');
      }
    } catch (err) {
      console.error('Error fetching auth codes:', err);
      setError(err as Error);
      setAuthCodes([]);
      setTotalCount(1);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, sortBy, sortOrder, authCode, policyNumber]);

  useEffect(() => {
    fetchAuthCodes();
  }, [
    page,
    pageSize,
    sortBy,
    sortOrder,
    authCode,
    policyNumber,
    fetchAuthCodes,
  ]);

  return {
    authCodes,
    loading,
    error,
    totalCount,
    totalPages,
    refetch: fetchAuthCodes,
  };
};
